import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import booksService from "@/features/books/services/booksService";
import { sessionsKeys } from "@/features/sessions/queries/sessionsQueryKeys";

export type UnscheduleBookVariables = {
    bookId: string;
    /** Defaults to `/books`. Use `/sessions` when unscheduling from the sessions month view. */
    redirectTo?: string;
};

/**
 * Mutation hook for unscheduling a book
 * Invalidates books and sessions queries on success
 */
export function useUnscheduleBook() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async ({ bookId }: UnscheduleBookVariables) => {
            return await booksService.unscheduleBook(bookId);
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : "Failed to unschedule book";
            toast.error(errorMessage);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: booksKeys.all });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.all });

            toast.success("Book unscheduled successfully");
            navigate(variables.redirectTo ?? "/books");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
